/**
 * Frontend Auth Utilities — Phase 4a Permission Awareness
 *
 * Provides utilities for reading permissions from JWT token and checking
 * whether the current user can perform actions.
 *
 * Note: Frontend permission checks are for usability only.
 * Backend enforcement is the true security boundary.
 */

/**
 * Decode JWT token (without verification - safe on frontend since verification happens on backend)
 * @param {string} token - JWT token
 * @returns {object} Decoded payload
 */
export function decodeToken(token) {
  try {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (e) {
    console.error('Failed to decode token:', e);
    return null;
  }
}

/**
 * Get current user info from token
 * @returns {object} User info with {user_id, tenant_id, email, role, is_owner}
 */
export function getCurrentUser() {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const payload = decodeToken(token);
  return payload;
}

/**
 * Get current user's role
 * @returns {string} Role (admin, administrator, underwriter, viewer)
 */
export function getCurrentUserRole() {
  const user = getCurrentUser();
  return user?.role || 'viewer';
}

/**
 * Permission catalog (mirrors backend)
 */
export const PERMISSIONS = {
  // Merchant
  MERCHANT_VIEW: 'merchant.view',
  MERCHANT_CREATE: 'merchant.create',
  MERCHANT_EDIT: 'merchant.edit',
  MERCHANT_DELETE: 'merchant.delete',

  // Owner
  OWNER_VIEW: 'owner.view',
  OWNER_CREATE: 'owner.create',
  OWNER_EDIT: 'owner.edit',
  OWNER_DELETE: 'owner.delete',
  OWNER_IDENTITY_SENSITIVE_VIEW_MASKED: 'owner.identity_sensitive.view_masked',
  OWNER_IDENTITY_SENSITIVE_EDIT: 'owner.identity_sensitive.edit',

  // Address
  ADDRESS_VIEW: 'address.view',
  ADDRESS_CREATE: 'address.create',
  ADDRESS_EDIT: 'address.edit',
  ADDRESS_DELETE: 'address.delete',

  // Bank Account
  BANK_ACCOUNT_VIEW: 'bank_account.view',
  BANK_ACCOUNT_CREATE: 'bank_account.create',
  BANK_ACCOUNT_EDIT: 'bank_account.edit',
  BANK_ACCOUNT_DELETE: 'bank_account.delete',

  // Additional Entity
  ADDITIONAL_ENTITY_VIEW: 'additional_entity.view',
  ADDITIONAL_ENTITY_CREATE: 'additional_entity.create',
  ADDITIONAL_ENTITY_EDIT: 'additional_entity.edit',
  ADDITIONAL_ENTITY_DELETE: 'additional_entity.delete',

  // Funding Entity
  FUNDING_ENTITY_VIEW: 'funding_entity.view',
  FUNDING_ENTITY_CREATE: 'funding_entity.create',
  FUNDING_ENTITY_EDIT: 'funding_entity.edit',
  FUNDING_ENTITY_DELETE: 'funding_entity.delete',

  // Deal
  DEAL_VIEW: 'deal.view',
  DEAL_CREATE: 'deal.create',
  DEAL_EDIT: 'deal.edit',
  DEAL_DELETE: 'deal.delete',
  DEAL_CHANGE_STATUS: 'deal.change_status',

  // Generation
  GENERATION_RUN: 'generation.run',
  GENERATION_DOWNLOAD: 'generation.download',
  GENERATION_VIEW_HISTORY: 'generation.view_history',

  // Administration
  USER_MANAGE: 'user.manage',
  SETTINGS_MANAGE: 'settings.manage',

  // Audit
  ACTION_LOG_VIEW: 'action_log.view',

  // Contract Template
  CONTRACT_TEMPLATE_VIEW: 'contract_template.view',
  CONTRACT_TEMPLATE_CREATE: 'contract_template.create',
  CONTRACT_TEMPLATE_EDIT: 'contract_template.edit',
};

/**
 * Default role permission bundles (mirrors backend)
 */
const ROLE_PERMISSIONS = {
  admin: new Set([
    PERMISSIONS.MERCHANT_VIEW, PERMISSIONS.MERCHANT_CREATE, PERMISSIONS.MERCHANT_EDIT, PERMISSIONS.MERCHANT_DELETE,
    PERMISSIONS.OWNER_VIEW, PERMISSIONS.OWNER_CREATE, PERMISSIONS.OWNER_EDIT, PERMISSIONS.OWNER_DELETE,
    PERMISSIONS.OWNER_IDENTITY_SENSITIVE_VIEW_MASKED, PERMISSIONS.OWNER_IDENTITY_SENSITIVE_EDIT,
    PERMISSIONS.ADDRESS_VIEW, PERMISSIONS.ADDRESS_CREATE, PERMISSIONS.ADDRESS_EDIT, PERMISSIONS.ADDRESS_DELETE,
    PERMISSIONS.BANK_ACCOUNT_VIEW, PERMISSIONS.BANK_ACCOUNT_CREATE, PERMISSIONS.BANK_ACCOUNT_EDIT, PERMISSIONS.BANK_ACCOUNT_DELETE,
    PERMISSIONS.ADDITIONAL_ENTITY_VIEW, PERMISSIONS.ADDITIONAL_ENTITY_CREATE, PERMISSIONS.ADDITIONAL_ENTITY_EDIT, PERMISSIONS.ADDITIONAL_ENTITY_DELETE,
    PERMISSIONS.FUNDING_ENTITY_VIEW, PERMISSIONS.FUNDING_ENTITY_CREATE, PERMISSIONS.FUNDING_ENTITY_EDIT, PERMISSIONS.FUNDING_ENTITY_DELETE,
    PERMISSIONS.DEAL_VIEW, PERMISSIONS.DEAL_CREATE, PERMISSIONS.DEAL_EDIT, PERMISSIONS.DEAL_DELETE, PERMISSIONS.DEAL_CHANGE_STATUS,
    PERMISSIONS.GENERATION_RUN, PERMISSIONS.GENERATION_DOWNLOAD, PERMISSIONS.GENERATION_VIEW_HISTORY,
    PERMISSIONS.CONTRACT_TEMPLATE_VIEW, PERMISSIONS.CONTRACT_TEMPLATE_CREATE, PERMISSIONS.CONTRACT_TEMPLATE_EDIT,
    PERMISSIONS.USER_MANAGE, PERMISSIONS.SETTINGS_MANAGE, PERMISSIONS.ACTION_LOG_VIEW,
  ]),
  underwriter: new Set([
    PERMISSIONS.MERCHANT_VIEW, PERMISSIONS.MERCHANT_CREATE, PERMISSIONS.MERCHANT_EDIT,
    PERMISSIONS.OWNER_VIEW, PERMISSIONS.OWNER_CREATE, PERMISSIONS.OWNER_EDIT, PERMISSIONS.OWNER_IDENTITY_SENSITIVE_VIEW_MASKED,
    PERMISSIONS.ADDRESS_VIEW, PERMISSIONS.ADDRESS_CREATE, PERMISSIONS.ADDRESS_EDIT,
    PERMISSIONS.BANK_ACCOUNT_VIEW, PERMISSIONS.BANK_ACCOUNT_CREATE, PERMISSIONS.BANK_ACCOUNT_EDIT,
    PERMISSIONS.ADDITIONAL_ENTITY_VIEW, PERMISSIONS.ADDITIONAL_ENTITY_CREATE, PERMISSIONS.ADDITIONAL_ENTITY_EDIT,
    PERMISSIONS.FUNDING_ENTITY_VIEW, PERMISSIONS.FUNDING_ENTITY_CREATE, PERMISSIONS.FUNDING_ENTITY_EDIT,
    PERMISSIONS.DEAL_VIEW, PERMISSIONS.DEAL_CREATE, PERMISSIONS.DEAL_EDIT, PERMISSIONS.DEAL_CHANGE_STATUS,
    PERMISSIONS.GENERATION_RUN, PERMISSIONS.GENERATION_DOWNLOAD, PERMISSIONS.GENERATION_VIEW_HISTORY,
    PERMISSIONS.CONTRACT_TEMPLATE_VIEW, PERMISSIONS.CONTRACT_TEMPLATE_CREATE, PERMISSIONS.CONTRACT_TEMPLATE_EDIT,
  ]),
  administrator: new Set([
    PERMISSIONS.MERCHANT_VIEW, PERMISSIONS.MERCHANT_EDIT,
    PERMISSIONS.OWNER_VIEW, PERMISSIONS.OWNER_EDIT, PERMISSIONS.OWNER_IDENTITY_SENSITIVE_VIEW_MASKED,
    PERMISSIONS.ADDRESS_VIEW, PERMISSIONS.ADDRESS_EDIT,
    PERMISSIONS.BANK_ACCOUNT_VIEW, PERMISSIONS.BANK_ACCOUNT_EDIT,
    PERMISSIONS.ADDITIONAL_ENTITY_VIEW, PERMISSIONS.ADDITIONAL_ENTITY_EDIT,
    PERMISSIONS.FUNDING_ENTITY_VIEW, PERMISSIONS.FUNDING_ENTITY_EDIT,
    PERMISSIONS.DEAL_VIEW,
    PERMISSIONS.GENERATION_RUN, PERMISSIONS.GENERATION_DOWNLOAD, PERMISSIONS.GENERATION_VIEW_HISTORY,
    PERMISSIONS.CONTRACT_TEMPLATE_VIEW,
    PERMISSIONS.USER_MANAGE, PERMISSIONS.ACTION_LOG_VIEW,
  ]),
  viewer: new Set([
    PERMISSIONS.MERCHANT_VIEW,
    PERMISSIONS.OWNER_VIEW, PERMISSIONS.OWNER_IDENTITY_SENSITIVE_VIEW_MASKED,
    PERMISSIONS.ADDRESS_VIEW,
    PERMISSIONS.BANK_ACCOUNT_VIEW,
    PERMISSIONS.ADDITIONAL_ENTITY_VIEW,
    PERMISSIONS.FUNDING_ENTITY_VIEW,
    PERMISSIONS.DEAL_VIEW,
    PERMISSIONS.GENERATION_VIEW_HISTORY,
    PERMISSIONS.CONTRACT_TEMPLATE_VIEW,
  ]),
};

/**
 * Get permissions for a role
 * @param {string} role - Role name
 * @returns {Set} Set of permission strings
 */
function getPermissionsForRole(role) {
  return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.viewer;
}

/**
 * Check if current user has a specific permission
 * @param {string} permission - Permission string
 * @returns {boolean} True if user has permission
 */
export function hasPermission(permission) {
  const role = getCurrentUserRole();
  const permissions = getPermissionsForRole(role);
  return permissions.has(permission);
}

/**
 * Check if current user has any of the specified permissions
 * @param {array} permissions - Array of permission strings
 * @returns {boolean} True if user has any permission
 */
export function hasAnyPermission(permissions) {
  return permissions.some(p => hasPermission(p));
}

/**
 * Check if current user has all specified permissions
 * @param {array} permissions - Array of permission strings
 * @returns {boolean} True if user has all permissions
 */
export function hasAllPermissions(permissions) {
  return permissions.every(p => hasPermission(p));
}

/**
 * Show a permission denied message (for UX feedback)
 * @param {string} action - Action that was denied (e.g., "create merchant")
 * @returns {string} User-friendly message
 */
export function getPermissionDeniedMessage(action) {
  return `You don't have permission to ${action}. Please contact your administrator.`;
}

/**
 * Handle 403 Forbidden response
 * @param {object} error - Error object from API response
 * @returns {object} User-friendly error message and details
 */
export function handle403Error(error) {
  const message = error?.response?.data?.detail || 'You do not have permission to perform this action.';
  return {
    statusCode: 403,
    message,
    userMessage: 'Permission Denied',
    userDetail: typeof message === 'string' ? message : 'You do not have permission to perform this action.',
  };
}

/**
 * Handle API errors with permission awareness
 * @param {object} error - Error from API call
 * @returns {object} Processed error with user-friendly messages
 */
export function handleAPIError(error) {
  // 403 Forbidden - permission denied
  if (error?.response?.status === 403) {
    return handle403Error(error);
  }

  // 401 Unauthorized - not authenticated or token expired
  if (error?.response?.status === 401) {
    // Clear token and redirect to login
    localStorage.removeItem('token');
    window.location.href = '/login';
    return {
      statusCode: 401,
      message: 'Session expired. Please log in again.',
      userMessage: 'Authentication Required',
      userDetail: 'Your session has expired. Please log in again.',
    };
  }

  // Other errors
  return {
    statusCode: error?.response?.status || 500,
    message: error?.response?.data?.detail || error?.message || 'An error occurred',
    userMessage: 'Error',
    userDetail: error?.response?.data?.detail || 'An error occurred. Please try again.',
  };
}
